import type { Draft, PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { PlanDTO } from '../../../Generated/PlanDTO.ts'
import type { CreditItemDTO } from '../../../Generated/CreditItemDTO.ts'
import type { CreditBalanceDTO } from '../../../Generated/CreditBalanceDTO.ts'
import type { CreditTransactionDTO } from '../../../Generated/CreditTransactionDTO.ts'

export type BillingLoadingState = 'initializing' | 'loading' | 'ready' | 'error'

export interface BillingState {
  plans: PlanDTO[]
  creditItems: CreditItemDTO[]
  balance: CreditBalanceDTO | null
  transactions: CreditTransactionDTO[]
  loadingState: BillingLoadingState
  /** A background credit refresh (reloadCredits) is in flight — drives the reload button spinner. */
  creditsReloading: boolean
}

export const initialState: BillingState = {
  plans: [],
  creditItems: [],
  balance: null,
  transactions: [],
  loadingState: 'initializing',
  creditsReloading: false
}

const reducers = {
  /** Request a full billing load (plans, credit items, and — if subscribed — balance + history).
   *  The `includeCredits` payload is consumed by LoadBillingEpic, not here. */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  loadBilling: (state: Draft<BillingState>, _action: PayloadAction<{ includeCredits: boolean }>) => {
    // Only show the spinner on the first load; later (re)loads refresh in the background.
    if (state.loadingState === 'initializing') state.loadingState = 'loading'
  },
  billingLoaded: (
    state: Draft<BillingState>,
    action: PayloadAction<{
      plans: PlanDTO[]
      creditItems: CreditItemDTO[]
      balance: CreditBalanceDTO | null
      transactions: CreditTransactionDTO[]
    }>
  ) => {
    state.plans = action.payload.plans
    state.creditItems = action.payload.creditItems
    state.balance = action.payload.balance
    state.transactions = action.payload.transactions
    state.loadingState = 'ready'
    state.creditsReloading = false
  },
  billingLoadFailed: (state: Draft<BillingState>, action: PayloadAction<{ error: unknown }>) => {
    console.error('failed to load billing data', action.payload.error)
    if (state.loadingState !== 'ready') state.loadingState = 'error'
    state.creditsReloading = false
  },
  /** Refresh just the credit balance + history — driven by the websocket credit event, the recharge
   *  poll, and the manual reload button. Does not flip the page into a (full) loading state. */
  reloadCredits: (state: Draft<BillingState>) => {
    state.creditsReloading = true
  },
  creditsLoaded: (
    state: Draft<BillingState>,
    action: PayloadAction<{ balance: CreditBalanceDTO; transactions: CreditTransactionDTO[] }>
  ) => {
    state.balance = action.payload.balance
    state.transactions = action.payload.transactions
    state.creditsReloading = false
  }
}

export const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers
})

export const { loadBilling, billingLoaded, billingLoadFailed, reloadCredits, creditsLoaded } = billingSlice.actions

type RootBillingState = { billing: BillingState }
export const selectBillingPlans = (s: RootBillingState) => s.billing.plans
export const selectBillingCreditItems = (s: RootBillingState) => s.billing.creditItems
export const selectBillingBalance = (s: RootBillingState) => s.billing.balance
export const selectBillingTransactions = (s: RootBillingState) => s.billing.transactions
export const selectBillingLoadingState = (s: RootBillingState) => s.billing.loadingState
export const selectBillingCreditsReloading = (s: RootBillingState) => s.billing.creditsReloading
